import React, { useCallback, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import {
  getPlotlyLegendStyle,
  lightenColor,
  darkenColor,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import { processBinData } from "../../graphView/processBinData";
import { buildNumberFrequencyDualYTicksDataBound } from "./axisTicks";
import {
  plotlyAxisBase,
  plotlyAxisBaseCategoricalX,
  plotlyAxisTitle,
} from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import {
  wrHundredCategoryFallbackShapes,
  wrHundredLegendTraceCategory,
} from "./plotlyReferenceLineLegend";
import { LABEL, WrLabel } from "../../../../utils/samplingDisplayLabels";
import "./wrVsLengthPlotly.css";

const CARD_W = 300;
const CARD_H_BAR = 260;
const CARD_H_WR = 280;
const STOCKED_COLORS = ["#8b5cf6", "#d946ef", "#7c3aed", "#a855f7", "#c026d3"];

export default function NumberFrequencyChart({
  graphData,
  selectedUnit,
  isMobile,
  selectedBinSize,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const binUnitLabel = selectedUnit.value === "English" ? "in" : "mm";

  const { labels, traces, dualY } = useMemo(() => {
    const sList = sortSpeciesForWeightLengthLegend([
      ...new Set(graphData.map((d) => d.species)),
    ]);
    const perSpecies = {};
    sList.forEach((s) => {
      perSpecies[s] = processBinData(
        graphData.filter((g) => g.species === s),
        { unitValue: selectedUnit.value, binSizeValue: selectedBinSize?.value }
      );
    });

    const allLabels = [
      ...new Set(
        Object.values(perSpecies)
          .flat()
          .map((b) => b.binLabel)
      ),
    ].sort((a, b) => parseFloat(a.split("-")[0]) - parseFloat(b.split("-")[0]));

    const combined = allLabels.map((binLabel) => {
      const row = { binLabel, totalNumber: 0 };
      sList.forEach((s) => {
        const b = perSpecies[s].find((x) => x.binLabel === binLabel);
        row[`${s}_culled`] = Math.floor(b?.culled || 0);
        row[`${s}_released`] = Math.floor(b?.released || 0);
        row[`${s}_stocking`] = Math.floor(b?.stocking || 0);
        row[`${s}_averageWr`] = b?.averageWr || 0;
        row[`${s}_stdev`] = b?.stdev || 0;
        row.totalNumber +=
          row[`${s}_culled`] + row[`${s}_released`] + row[`${s}_stocking`];
      });
      let tw = 0;
      let tn = 0;
      sList.forEach((s) => {
        const n = row[`${s}_culled`] + row[`${s}_released`];
        tw += (row[`${s}_averageWr`] || 0) * n;
        tn += n;
      });
      row.overallAverageWr = tn > 0 ? tw / tn : 0;
      return row;
    });

    const hasStockingBars =
      graphData.some((d) => d.isStocking) ||
      combined.some((row) => sList.some((s) => (row[`${s}_stocking`] || 0) > 0));

    const builtTraces = [];
    sList.forEach((s) => {
      const { color: c } = getPlotlyLegendStyle(s, sList);
      const wc = darkenColor(c, 35);
      const lightC = lightenColor(c, 50);
      const stockedColor = STOCKED_COLORS[sList.indexOf(s) % STOCKED_COLORS.length];

      builtTraces.push({
        type: "bar",
        name: `${s} Culled`,
        x: allLabels,
        y: combined.map((r) => r[`${s}_culled`]),
        customdata: combined.map((r) => [
          r.binLabel,
          s,
          "Culled",
          r[`${s}_culled`],
          r[`${s}_released`] + r[`${s}_stocking`],
          r.totalNumber,
          c,
        ]),
        marker: { color: c },
        yaxis: "y",
        hovertemplate: "<extra></extra>",
      });
      builtTraces.push({
        type: "bar",
        name: `${s} Released`,
        x: allLabels,
        y: combined.map((r) => r[`${s}_released`]),
        customdata: combined.map((r) => [
          r.binLabel,
          s,
          "Released",
          r[`${s}_released`],
          hasStockingBars
            ? r[`${s}_culled`] + r[`${s}_stocking`]
            : r[`${s}_culled`],
          r.totalNumber,
          lightC,
        ]),
        marker: { color: lightC },
        yaxis: "y",
        hovertemplate: "<extra></extra>",
      });
      if (hasStockingBars) {
        builtTraces.push({
          type: "bar",
          name: `${s} Stocked`,
          x: allLabels,
          y: combined.map((r) => r[`${s}_stocking`]),
          customdata: combined.map((r) => [
            r.binLabel,
            s,
            "Stocked",
            r[`${s}_stocking`],
            r[`${s}_culled`] + r[`${s}_released`],
            r.totalNumber,
            stockedColor,
          ]),
          marker: {
            color: stockedColor,
            line: { color: "#4c1d95", width: 1.5 },
          },
          yaxis: "y",
          hovertemplate: "<extra></extra>",
        });
      }
      builtTraces.push({
        type: "scatter",
        mode: "lines+markers",
        name: `${s} ${LABEL.WR_PCT_HTML}`,
        x: allLabels,
        // null for bins with no fish so the line starts/ends at real data (no drop to 0).
        y: combined.map((r) => {
          const n = (r[`${s}_culled`] || 0) + (r[`${s}_released`] || 0);
          if (n <= 0) return null;
          const wr = Number(r[`${s}_averageWr`]);
          return Number.isFinite(wr) ? wr : null;
        }),
        error_y: {
          type: "data",
          array: combined.map((r) => {
            const n = (r[`${s}_culled`] || 0) + (r[`${s}_released`] || 0);
            if (n <= 0) return null;
            return r[`${s}_stdev`] || 0;
          }),
          visible: true,
        },
        connectgaps: false,
        customdata: combined.map((r) => [
          r.binLabel,
          s,
          Number(r[`${s}_stdev`] || 0),
          wc,
        ]),
        line: { color: wc, width: 2 },
        marker: { color: wc, size: 6 },
        yaxis: "y2",
        hovertemplate: "<extra></extra>",
      });
    });

    const leftMaxObs = Math.max(...combined.map((r) => r.totalNumber), 0);
    const wrRatioSamples = [];
    combined.forEach((row) => {
      sList.forEach((s) => {
        const n = (row[`${s}_culled`] || 0) + (row[`${s}_released`] || 0);
        if (n <= 0) return;
        const w = Number(row[`${s}_averageWr`]);
        if (!Number.isFinite(w)) return;
        const st = Number(row[`${s}_stdev`]) || 0;
        wrRatioSamples.push(w, w - st, w + st);
      });
    });
    const dualY = buildNumberFrequencyDualYTicksDataBound(leftMaxObs, wrRatioSamples);

    const refTrace = wrHundredLegendTraceCategory(allLabels, { yaxis: "y2" });
    if (refTrace) builtTraces.push(refTrace);

    return {
      labels: allLabels,
      traces: builtTraces,
      dualY,
    };
  }, [graphData, selectedUnit.value, selectedBinSize?.value]);

  const legendLayout = useMemo(
    () =>
      plotlyHorizontalLegendForTraces(traces, isMobile, {
        font: { size: isMobile ? 14 : 17, color: "#000000" },
        legendFontSize: isMobile ? 14 : 17,
        legendEntryWidthPx: isMobile ? 176 : 232,
      }),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 620,
    desktopDefault: 760,
    legendExtra: legendLayout.extraHeight || 0,
    fullscreenReserved: isMobile ? 270 : 235,
  });

  const onHover = useCallback((eventData) => {
    const pt = eventData?.points?.[0];
    const el = wrapRef.current;
    const evt = eventData?.event;
    if (!pt || el == null) {
      return;
    }

    let cx;
    let cy;
    if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
      cx = evt.clientX;
      cy = evt.clientY;
    } else if (pt.bbox) {
      cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
      cy = pt.bbox.y0;
    } else {
      return;
    }

    const traceType = pt.data?.type || pt.fullData?.type;
    const rect = el.getBoundingClientRect();
    const pad = 12;

    if (traceType === "bar") {
      const cd = pt.customdata;
      if (!Array.isArray(cd) || cd.length < 7) {
        return;
      }
      const binLabel = String(cd[0] ?? "");
      const species = String(cd[1] ?? "");
      const segment = String(cd[2] ?? "");
      const countThis = Number(cd[3]);
      const countOther = Number(cd[4]);
      const totalInBin = Number(cd[5]);
      const segmentColor = String(cd[6] ?? "#059669");

      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H_BAR > rect.height - 8) {
        top = cy - rect.top - CARD_H_BAR - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H_BAR - 8));

      setTip({
        kind: "bar",
        left,
        top,
        binLabel,
        species,
        segment,
        segmentColor,
        countThis,
        countOther,
        totalInBin,
      });
      return;
    }

    if (traceType === "scatter") {
      const traceMode = String(pt.data?.mode || pt.fullData?.mode || "");
      if (!traceMode.includes("markers")) {
        return;
      }
      if (typeof pt.y !== "number" || !Number.isFinite(pt.y)) {
        return;
      }
      const cd = pt.customdata;
      if (!Array.isArray(cd) || cd.length < 4) {
        return;
      }
      const binLabel = String(cd[0] ?? "");
      const species = String(cd[1] ?? "");
      const stdev = Number(cd[2]);
      const lineColor = String(cd[3] ?? "#2563eb");
      const avgWr = pt.y;
      const aboveStandard = avgWr >= 1;

      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H_WR > rect.height - 8) {
        top = cy - rect.top - CARD_H_WR - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H_WR - 8));

      setTip({
        kind: "wr",
        left,
        top,
        binLabel,
        species,
        avgWr,
        stdev,
        lineColor,
        aboveStandard,
      });
    }
  }, []);

  const onUnhover = useCallback(() => setTip(null), []);

  return (
    <div
      ref={wrapRef}
      className="number-frequency-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 80, b: isMobile ? 170 : 150, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          shapes: wrHundredCategoryFallbackShapes(labels, { yaxis: "y2" }),
          barmode: "stack",
          xaxis: {
            tickangle: -90,
            categoryorder: "array",
            categoryarray: labels,
            ...plotlyAxisBaseCategoricalX(isMobile),
            tickfont: { size: isMobile ? 12 : 14, color: "#000000" },
            title: {
              ...plotlyAxisTitle(
                `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"})`,
                isMobile
              ),
              font: { size: isMobile ? 16 : 20, color: "#000000", weight: "bold" },
            },
          },
          yaxis: {
            tickmode: "array",
            tickvals: dualY.leftTicks,
            range: [0, dualY.leftUpper],
            ...plotlyAxisBase(isMobile),
            tickfont: { size: isMobile ? 12 : 14, color: "#000000" },
            title: {
              ...plotlyAxisTitle(LABEL.NUMBER_FREQUENCY, isMobile),
              font: { size: isMobile ? 16 : 20, color: "#000000", weight: "bold" },
            },
          },
          yaxis2: {
            overlaying: "y",
            side: "right",
            tickmode: "array",
            tickvals: dualY.rightTicks,
            ticktext: dualY.rightTicks.map((v) => `${Math.round(v * 100)}%`),
            range: [dualY.wrYMin, dualY.wrYMax],
            ...plotlyAxisBase(isMobile),
            tickfont: { size: isMobile ? 12 : 14, color: "#000000" },
            title: {
              ...plotlyAxisTitle(LABEL.AVG_WR_PCT_HTML, isMobile),
              font: { size: isMobile ? 16 : 20, color: "#000000", weight: "bold" },
            },
            showgrid: false,
          },
          legend: legendLayout.legend,
          hovermode: "closest",
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip?.kind === "bar" && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--nf${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.segmentColor }}
                aria-hidden
              />
              {tip.species}
            </p>
            <p className="wr-length-hover-card__title-line">{tip.segment}</p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length bin:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.binLabel} ({binUnitLabel})
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">This segment:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {Math.round(tip.countThis)} fish
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                {tip.segment === "Stocked"
                  ? "Sampling (culled + released):"
                  : tip.segment === "Culled"
                    ? "Other (released + stocked):"
                    : "Other (culled + stocked):"}
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {Math.round(tip.countOther)} fish
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Total in bin:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--count">
                {Math.round(tip.totalInBin)} fish
              </span>
            </div>
          </div>
        </div>
      )}
      {tip?.kind === "wr" && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--nf${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.lineColor }}
                aria-hidden
              />
              {tip.species} — Average <WrLabel />
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length bin:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.binLabel} ({binUnitLabel})
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                Average <WrLabel />:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--wr">
                {(tip.avgWr * 100).toFixed(1)}%
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">{LABEL.STD_DEV}:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {Number.isFinite(tip.stdev) ? tip.stdev.toFixed(3) : "—"}
              </span>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard ? (
              <>
                ✓ Bin average at or above <WrLabel /> = 1.0
              </>
            ) : (
              <>
                Bin average below <WrLabel /> = 1.0
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
